import React, { useContext } from 'react'
import PropTypes from 'prop-types'

import { CartsAppContext } from '../context/carts-app-context'

/**
 * @description encapsulates an "Order Note" field, with a title, description, and input.
 * the input updates the cart's "notes" property on change
 * @param props {Object} the component's props
 * @param props.title {string} the title for the note input
 * @param props.description {string} the description for the note input
 * @param props.required {boolean} whether this note must be filled out
 * @param props.forEvent {number} the external ID of the associated performance
 * @returns JSX.Element
 * @constructor
 */
const OrderNote = props => {
  const { forEvent, required, title, description } = props
  const {
    cart,
    actions: { setCart }
  } = useContext(CartsAppContext)

  /**
   * listens for a change on the input and updates the respective note in state.
   * @param e {Event} the change event. carries the input name and value.
   * @param cart {Object} the current state of the cart
   * @param setCart {Function} a setter for the root component's cart state.
   */
  const handleNoteTextChange = (e, cart, setCart) => {
    const { name, value } = e.target
    let newCart = {}
    if (!value.trim()) {
      // remove key only if note is required (isMissingRequiredNotes method in logic.js depends on this)
      if (cart.items.find(item => +item.externalId === +name).noteIsRequired) {
        const notes = { ...cart.notes }
        delete notes[name]
        newCart = { ...cart, notes }
      } else {
        newCart = {
          ...cart,
          notes: {
            ...cart.notes,
            [name]: ''
          }
        }
      }
    } else {
      // otherwise, set this key to this value on the notes object
      newCart = {
        ...cart,
        notes: {
          ...cart.notes,
          [name]: value.trim()
        }
      }
    }

    setCart(newCart)
  }

  return (
    <div data-testid="note-container">
      <h4
        id="note-title"
        className="text-18"
      >
        {title} {required && '*'}
      </h4>
      <p className="text-16">{description}</p>
      <input
        name={forEvent}
        data-testid={`note-${forEvent}`}
        className="order-note"
        type="text"
        defaultValue={cart.notes[forEvent]}
        required={required}
        aria-required={required}
        aria-labelledby="note-title"
        onChange={event => handleNoteTextChange(event, cart, setCart)}
      />
      {required && <span className="text-12">* This field is required</span>}
    </div>
  )
}

OrderNote.defaultProps = {
  forEvent: 0,
  required: false,
  title: 'Order Note',
  description: ''
}

OrderNote.propTypes = {
  forEvent: PropTypes.number, // this is the associated performance for this note
  required: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string
}

export default OrderNote
